import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'src/hooks/use-ui';
import { setNudgeIntentMarker } from 'src/components/modals/phone-verification-nudge/nudge-intent-marker';
import { trackIntentMarkerSet } from 'src/components/modals/phone-verification-nudge/nudge-intent-marker-analytics';
import { EMBEDDED_OAUTH_POPUP_HANDOFF_FLAG } from 'src/oauth/embedded-oauth-handoff-flag';
import { SocialLoginProps } from './social-login.types';

export const SocialLogin = ({
  redirectUrl,
  autoLogin = false,
  isEmbeddedPopup = false,
  onSubmit,
  className,
  icon,
  provider,
  buttonText,
  publicContextSessionId = null,
}: SocialLoginProps): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const { isEmbedded } = useUI();
  const flags = useFlags();
  const isEmbeddedOAuthPopupHandoffEnabled = flags[EMBEDDED_OAUTH_POPUP_HANDOFF_FLAG] ?? false;
  const buttonTestId = `social-login-button-${provider}`;
  const formTestId = `social-login-form-${provider}`;

  useEffect(() => {
    if (autoLogin && formRef.current) {
      formRef.current.submit();
    }
  }, [autoLogin]);

  const handleEmbeddedMenuLogin = (): void => {
    if (!autoLogin) {
      setNudgeIntentMarker();
      trackIntentMarkerSet();
    }
    onSubmit?.();

    // Preserve the legacy URL construction while the rollout is off. Encoding this branch would
    // change how existing redirect URLs with query parameters are parsed before the fix is enabled.
    const autoLoginUrl = isEmbeddedOAuthPopupHandoffEnabled
      ? `/oauth/auto-login?${new URLSearchParams({ provider, redirectUrl, embeddedPopup: 'true' }).toString()}`
      : `/oauth/auto-login?provider=${provider}&redirectUrl=${redirectUrl}`;

    window.open(autoLoginUrl, '_blank', 'width=400,height=600,toolbar=0,location=0,menubar=0');
  };

  // For the initial embedded menu button, we need to open a new popup window instead of submitting form
  if (isEmbedded && !autoLogin) {
    return (
      <Button type='button' onClick={handleEmbeddedMenuLogin} className={className} data-testid={buttonTestId}>
        {icon}
        <span>{buttonText}</span>
      </Button>
    );
  }

  const handleFormSubmit = (): void => {
    // Defensive: the `autoLogin` effect above submits via `formRef.current.submit()`,
    // which is a native submit and does NOT fire this React `onSubmit` handler. This
    // gate is kept anyway so the marker can never be set on the auto-login path even
    // if that submit mechanism changes -- getting this wrong would silently break the
    // `autoLogin=true` deep-link acceptance criterion.
    if (!autoLogin) {
      setNudgeIntentMarker();
      trackIntentMarkerSet();
    }
    onSubmit?.();
  };

  return (
    <>
      <form
        method='post'
        action={`/api/v2/auth/${provider}`}
        ref={formRef}
        onSubmit={handleFormSubmit}
        className={className}
        data-testid={formTestId}
      >
        <input type='hidden' name='redirect_url' value={redirectUrl} />
        {isEmbeddedPopup && <input type='hidden' name='embedded_popup' value='true' />}
        {publicContextSessionId && (
          <input type='hidden' name='public_context_session_id' value={publicContextSessionId} />
        )}
        {!autoLogin && (
          <Button type='submit'>
            {icon}
            <span>{buttonText}</span>
          </Button>
        )}
      </form>
    </>
  );
};

const Button = styled.button`
  align-items: center;
  appearance: none;
  background: var(--dcm-color-surface, #fff);
  border-radius: ${({ theme }) => theme.customized.radius.buttons};
  border: none;
  box-shadow: 0 0 0 1px var(--dcm-color-border, #00000033);
  color: var(--dcm-color-text-primary, #1f1f1f);
  cursor: pointer;
  display: flex;
  font: 500 14px / ${20 / 14} 'Roboto', 'Matter', arial, sans-serif;
  gap: 8px;
  justify-content: center;
  padding: 15px;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: var(--dcm-color-surface-hover, #f0f0f0);
    box-shadow: 0 0 0 1px var(--dcm-color-border, #00000033), 0 1px 2px 0 #3c40434d, 0px 1px 3px 1px #3c404326;
  }
`;
